// Minimal polyfills for modern browsers
import logger from './logger';
import { getDeviceInfo } from './deviceDetection';

// ResizeObserver polyfill check
if (typeof ResizeObserver === 'undefined') {
  logger.warn('ResizeObserver not supported');
}

// IntersectionObserver polyfill check
if (typeof IntersectionObserver === 'undefined') {
  logger.warn('IntersectionObserver not supported');
}

// requestAnimationFrame polyfill for older browsers
if (!window.requestAnimationFrame) {
  window.requestAnimationFrame = (callback) => window.setTimeout(callback, 1000 / 60);
}

if (!window.cancelAnimationFrame) {
  window.cancelAnimationFrame = (id) => window.clearTimeout(id);
}

// Touch events normalization
export const normalizeTouch = (e: TouchEvent | MouseEvent): { x: number; y: number } => {
  if ('touches' in e && e.touches.length > 0) {
    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
  if ('clientX' in e) {
    return { x: e.clientX, y: e.clientY };
  }
  return { x: 0, y: 0 };
};

// Pointer events fallback
export const getPointerEvent = () => {
  if ('PointerEvent' in window) {
    return {
      start: 'pointerdown',
      move: 'pointermove',
      end: 'pointerup',
      cancel: 'pointercancel'
    };
  }
  if ('ontouchstart' in window) {
    return {
      start: 'touchstart',
      move: 'touchmove',
      end: 'touchend',
      cancel: 'touchcancel'
    };
  }
  return {
    start: 'mousedown',
    move: 'mousemove',
    end: 'mouseup',
    cancel: 'mouseleave'
  };
};

// Vibration API with fallback
export const vibrate = (pattern: number | number[]) => {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
};

// Screen Wake Lock API with fallback
let wakeLockRequest: Promise<any> | null = null;

export const requestWakeLock = async () => {
  if ('wakeLock' in navigator) {
    try {
      // Check if page is visible before requesting wake lock
      if (document.visibilityState !== 'visible') {
        logger.warn('⚠️ Wake Lock not requested - page is not visible. Will retry when page becomes visible.');
        
        // Set up listener to retry when page becomes visible
        if (!wakeLockRequest) {
          wakeLockRequest = new Promise((resolve) => {
            const handleVisibilityChange = async () => {
              if (document.visibilityState === 'visible') {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                try {
                  const wakeLock = await (navigator as any).wakeLock.request('screen');
                  logger.log('💡 Wake Lock acquired after page became visible');
                  resolve(wakeLock);
                } catch (err) {
                  logger.warn('⚠️ Wake Lock retry failed:', err);
                  resolve(null);
                }
              }
            };
            document.addEventListener('visibilitychange', handleVisibilityChange);
          });
        }
        return await wakeLockRequest;
      }
      
      const wakeLock = await (navigator as any).wakeLock.request('screen');
      wakeLockRequest = null;
      return wakeLock;
    } catch (err) {
      logger.warn('⚠️ Wake Lock not available:', err);
    }
  }
  return null;
};

// Clipboard API with fallback
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      logger.warn('Clipboard API failed:', err);
    }
  }
  
  // Fallback
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  } catch {
    document.body.removeChild(textArea);
    return false;
  }
};

// Share API with fallback
export const share = async (data: { title?: string; text?: string; url?: string }): Promise<boolean> => {
  if (navigator.share) {
    try {
      await navigator.share(data);
      return true;
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
      logger.warn('Share API failed:', err);
      }
      return false;
    }
  }
  return false;
};

// Fullscreen API with vendor prefixes
export const requestFullscreen = async (element: HTMLElement): Promise<boolean> => {
  try {
    if (element.requestFullscreen) {
      await element.requestFullscreen();
      return true;
    } else if ((element as any).webkitRequestFullscreen) {
      await (element as any).webkitRequestFullscreen();
      return true;
    } else if ((element as any).mozRequestFullScreen) {
      await (element as any).mozRequestFullScreen();
      return true;
    } else if ((element as any).msRequestFullscreen) {
      await (element as any).msRequestFullscreen();
      return true;
    }
  } catch (err) {
    logger.warn('Fullscreen request failed:', err);
  }
  return false;
};

export const exitFullscreen = async (): Promise<boolean> => {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
      return true;
    } else if ((document as any).webkitExitFullscreen) {
      await (document as any).webkitExitFullscreen();
      return true;
    } else if ((document as any).mozCancelFullScreen) {
      await (document as any).mozCancelFullScreen();
      return true;
    } else if ((document as any).msExitFullscreen) {
      await (document as any).msExitFullscreen();
      return true;
    }
  } catch (err) {
    logger.warn('Exit fullscreen failed:', err);
  }
  return false;
};

export const isFullscreen = (): boolean => {
  // Check for actual fullscreen API
  const hasFullscreen = !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement
  );

  // If not in actual fullscreen, check if we should simulate fullscreen for Safari mobile PWA
  if (!hasFullscreen) {
    const device = getDeviceInfo();
    return device.isIOS && device.isSafari && window.matchMedia('(display-mode: standalone)').matches;
  }

  return hasFullscreen;
};

// Orientation Lock API
export const lockOrientation = async (orientation: 'landscape' | 'portrait'): Promise<boolean> => {
  if (!window.screen?.orientation || !(window.screen.orientation as any).lock) {
    logger.warn('Screen Orientation Lock API not supported');
    return false;
  }
  try {
    await (window.screen.orientation as any).lock(orientation);
    return true;
  } catch (err) {
    logger.warn(`Failed to lock orientation to ${orientation}:`, err);
    return false;
  }
};

export const unlockOrientation = (): void => {
  if (window.screen?.orientation && (window.screen.orientation as any).unlock) {
    (window.screen.orientation as any).unlock();
  }
};

// Battery API (Battery Status API)
export const getBatteryStatus = async (): Promise<{
  level: number;
  charging: boolean;
  chargingTime: number;
  dischargingTime: number;
} | null> => {
  const nav = navigator as any;
  if (!nav.getBattery && !nav.battery) {
    return null;
  }

  try {
    const battery = nav.getBattery ? await nav.getBattery() : nav.battery;
    return {
      level: battery.level,
      charging: battery.charging,
      chargingTime: battery.chargingTime,
      dischargingTime: battery.dischargingTime,
    };
  } catch (err) {
    logger.warn('Battery API failed:', err);
    return null;
  }
};

// Low Power Mode detection (iOS/macOS specific)
export const isLowPowerMode = (): boolean => {
  if (!window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-data)').matches;
};

// Get system power preferences
export const getPowerPreference = (): 'low-power' | 'normal' | 'unknown' => {
  if (window.matchMedia('(prefers-reduced-motion)').matches) {
    return 'low-power';
  }
  const nav = navigator as any;
  if (nav.deviceMemory && nav.deviceMemory < 4) {
    return 'low-power';
  }
  if (!navigator.hardwareConcurrency || navigator.hardwareConcurrency < 4) {
    return 'low-power';
  }
  return 'normal';
};
